import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { AgentConfig, AgentId, ConversationState, StartConversationRequest } from './api.types';

type SocketStatus = 'connecting' | 'connected' | 'disconnected';

@Injectable({ providedIn: 'root' })
export class A2ASocketService {
  readonly state$ = new BehaviorSubject<ConversationState | null>(null);
  readonly status$ = new BehaviorSubject<SocketStatus>('disconnected');
  readonly error$ = new BehaviorSubject<string>('');

  private socket: WebSocket | null = null;
  private reconnectTimer: number | null = null;
  private readonly productionHost = 'agent-management.egm-robotics.com';

  constructor(private readonly zone: NgZone) {}

  connect() {
    if (this.socket?.readyState === WebSocket.OPEN || this.socket?.readyState === WebSocket.CONNECTING) {
      return;
    }

    this.status$.next('connecting');
    this.socket = new WebSocket(this.websocketUrl());

    this.socket.onopen = () => this.zone.run(() => {
      this.status$.next('connected');
      this.error$.next('');
    });

    this.socket.onmessage = (event) => this.zone.run(() => {
      const message = JSON.parse(event.data);
      if (message.type === 'state') {
        this.state$.next(message.state as ConversationState);
      }
      if (message.type === 'error') {
        this.error$.next(message.message as string);
      }
    });

    this.socket.onerror = () => this.zone.run(() => {
      this.error$.next('Error en la conexion WebSocket A2A.');
    });

    this.socket.onclose = () => this.zone.run(() => {
      this.status$.next('disconnected');
      this.socket = null;
      this.scheduleReconnect();
    });
  }

  disconnect() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.socket?.close();
    this.socket = null;
  }

  startAgent(agent: AgentConfig) {
    this.send({ type: 'agent.start', agent });
  }

  stopAgent(agentId: AgentId) {
    this.send({ type: 'agent.stop', agentId });
  }

  connectChannel() {
    this.send({ type: 'channel.connect' });
  }

  disconnectChannel() {
    this.send({ type: 'channel.disconnect' });
  }

  startConversation(payload: StartConversationRequest) {
    this.send({ type: 'conversation.start', payload });
  }

  finishConversation() {
    this.send({ type: 'conversation.finish' });
  }

  stepConversation() {
    this.send({ type: 'conversation.step' });
  }

  setAuto(auto: boolean) {
    this.send({ type: 'conversation.auto', auto });
  }

  private send(payload: unknown) {
    if (this.socket?.readyState !== WebSocket.OPEN) {
      this.error$.next('WebSocket A2A no conectado.');
      this.connect();
      return;
    }

    this.socket.send(JSON.stringify(payload));
  }

  private scheduleReconnect() {
    if (this.reconnectTimer) return;

    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, 1200);
  }

  private websocketUrl() {
    const isLocalAngular = ['localhost', '127.0.0.1'].includes(location.hostname) && location.port === '4200';
    const host = isLocalAngular ? this.productionHost : location.host;
    const protocol = isLocalAngular || location.protocol === 'https:' ? 'wss' : 'ws';
    return `${protocol}://${host}/ws/a2a`;
  }
}
